import { type SendInvoiceEmailMutation } from "~/gql/mutations/invoiceEmailDraft.generated";
import {
	type ClearOrderEmailRecipientRoutingMutation,
	type SetOrderEmailRecipientRoutingMutation
} from "~/gql/mutations/orderEmailRecipientRouting.generated";
import {
	type SetInvoiceEmailChannelMutation,
	type SetOrderEmailEnablementMutation,
	type SetOrderEmailSettingsMutation
} from "~/gql/mutations/orderEmailSettings.generated";
import {
	type ClearEmailSuppressionMutation,
	type CreateReportFolderMutation,
	type CreateReportMutation,
	type CreateReportScheduleMutation,
	type DeleteReportFolderMutation,
	type DeleteReportMutation,
	type DeleteReportScheduleMutation,
	type RenameReportFolderMutation,
	type RevertReportMutation,
	type RunReportMutation,
	type SetReportAppTagsMutation,
	type SetReportScheduleEmailSettingsMutation,
	type UpdateReportMutation,
	type UpdateReportScheduleMutation
} from "~/gql/mutations/reports.generated";
import {
	type GetCashReceiptsQuery,
	type GetCustomerAvgDaysToPayQuery,
	type GetCustomerSalesPaymentsCreditsQuery
} from "~/gql/queries/cashCollectionsReports.generated";
import { type GetOrderEmailNotificationCatalogQuery } from "~/gql/queries/orderEmailNotificationCatalog.generated";
import { type GetOrderEmailRecipientRoutingQuery } from "~/gql/queries/orderEmailRecipientRouting.generated";
import { type GetOrderEmailSettingsQuery } from "~/gql/queries/orderEmailSettings.generated";
import { type GetOrderTextHistoryQuery } from "~/gql/queries/orderTextHistory.generated";
import {
	type GenerateReportCsvQuery,
	type GenerateReportExcelQuery,
	type GenerateReportPdfQuery,
	type GetEmailSendDetailQuery,
	type GetEmailSendsQuery,
	type GetOrderEmailSchedulesQuery,
	type GetReportByIdQuery,
	type GetReportCurrentVersionQuery,
	type GetReportDataSourcesQuery,
	type GetReportExecutionByIdQuery,
	type GetReportExecutionsQuery,
	type GetReportFoldersQuery,
	type GetReportSchedulesQuery,
	type GetReportsQuery,
	type GetReportTemplateSummariesQuery,
	type GetReportTemplatesQuery,
	type GetReportVersionsQuery,
	type ReportsByAppTagQuery
} from "~/gql/queries/reports.generated";
import {
	type GetCustomerAgingQuery,
	type GetSalesComparisonQuery
} from "~/gql/queries/salesPerformanceReports.generated";

export const reportsEndpoints = {
	ClearEmailSuppression: {
		invalidatesTags: ["EmailSends"] as AddTagTypes,
		transformResponse: (res: ClearEmailSuppressionMutation) => res
	},
	ClearOrderEmailRecipientRouting: {
		invalidatesTags: ["OrderEmailRecipientRouting"] as AddTagTypes,
		transformResponse: (res: ClearOrderEmailRecipientRoutingMutation) => res
	},
	CreateReport: {
		invalidatesTags: ["Reports"] as AddTagTypes,
		transformResponse: (res: CreateReportMutation) => res
	},
	CreateReportFolder: {
		invalidatesTags: ["ReportFolders"] as AddTagTypes,
		transformResponse: (res: CreateReportFolderMutation) => res
	},
	CreateReportSchedule: {
		invalidatesTags: ["EmailSends", "ReportSchedules"] as AddTagTypes,
		transformResponse: (res: CreateReportScheduleMutation) => res
	},
	DeleteReport: {
		invalidatesTags: ["Reports"] as AddTagTypes,
		transformResponse: (res: DeleteReportMutation) => res
	},
	DeleteReportFolder: {
		invalidatesTags: ["ReportFolders"] as AddTagTypes,
		transformResponse: (res: DeleteReportFolderMutation) => res
	},
	DeleteReportSchedule: {
		invalidatesTags: ["ReportSchedules"] as AddTagTypes,
		transformResponse: (res: DeleteReportScheduleMutation) => res
	},
	GenerateReportCsv: {
		providesTags: ["Reports"] as AddTagTypes,
		transformResponse: (res: GenerateReportCsvQuery) => res
	},
	GenerateReportExcel: {
		providesTags: ["Reports"] as AddTagTypes,
		transformResponse: (res: GenerateReportExcelQuery) => res
	},
	GenerateReportPdf: {
		providesTags: ["Reports"] as AddTagTypes,
		transformResponse: (res: GenerateReportPdfQuery) => res
	},
	GetCashReceipts: {
		providesTags: ["ReportCashReceiptsData"] as AddTagTypes,
		transformResponse: (res: GetCashReceiptsQuery) => res
	},
	GetCustomerAging: {
		providesTags: ["ReportCustomerAgingData"] as AddTagTypes,
		transformResponse: (res: GetCustomerAgingQuery) => res
	},
	GetCustomerAvgDaysToPay: {
		providesTags: ["ReportCustomerAvgDaysToPayData"] as AddTagTypes,
		transformResponse: (res: GetCustomerAvgDaysToPayQuery) => res
	},
	GetCustomerSalesPaymentsCredits: {
		providesTags: ["ReportCustomerSalesPaymentsCreditsData"] as AddTagTypes,
		transformResponse: (res: GetCustomerSalesPaymentsCreditsQuery) => res
	},
	GetEmailSendDetail: {
		providesTags: ["EmailSends"] as AddTagTypes,
		transformResponse: (res: GetEmailSendDetailQuery) => res
	},
	GetEmailSends: {
		providesTags: ["EmailSends"] as AddTagTypes,
		transformResponse: (res: GetEmailSendsQuery) => res
	},
	GetOrderEmailRecipientRouting: {
		providesTags: ["OrderEmailRecipientRouting"] as AddTagTypes,
		transformResponse: (res: GetOrderEmailRecipientRoutingQuery) => res
	},
	GetOrderEmailSchedules: {
		providesTags: ["ReportSchedules"] as AddTagTypes,
		transformResponse: (res: GetOrderEmailSchedulesQuery) => res
	},
	GetOrderEmailNotificationCatalog: {
		providesTags: ["OrderEmailSettings"] as AddTagTypes,
		transformResponse: (res: GetOrderEmailNotificationCatalogQuery) => res
	},
	GetOrderEmailSettings: {
		providesTags: ["OrderEmailSettings"] as AddTagTypes,
		transformResponse: (res: GetOrderEmailSettingsQuery) => res
	},
	GetOrderTextHistory: {
		providesTags: ["EmailSends"] as AddTagTypes,
		transformResponse: (res: GetOrderTextHistoryQuery) => res
	},
	GetReportById: {
		providesTags: ["Reports"] as AddTagTypes,
		transformResponse: (res: GetReportByIdQuery) => res
	},
	GetReportCurrentVersion: {
		providesTags: ["ReportVersions"] as AddTagTypes,
		transformResponse: (res: GetReportCurrentVersionQuery) => res
	},
	GetReportDataSources: {
		providesTags: ["ReportDataSources"] as AddTagTypes,
		transformResponse: (res: GetReportDataSourcesQuery) => res
	},
	GetReportExecutionById: {
		providesTags: ["ReportExecutions"] as AddTagTypes,
		transformResponse: (res: GetReportExecutionByIdQuery) => res
	},
	GetReportExecutions: {
		providesTags: ["ReportExecutions"] as AddTagTypes,
		transformResponse: (res: GetReportExecutionsQuery) => res
	},
	GetReportFolders: {
		providesTags: ["ReportFolders"] as AddTagTypes,
		transformResponse: (res: GetReportFoldersQuery) => res
	},
	GetReports: {
		providesTags: ["Reports"] as AddTagTypes,
		transformResponse: (res: GetReportsQuery) => res
	},
	GetReportSchedules: {
		providesTags: ["ReportSchedules"] as AddTagTypes,
		transformResponse: (res: GetReportSchedulesQuery) => res
	},
	GetReportTemplates: {
		providesTags: ["ReportTemplates"] as AddTagTypes,
		transformResponse: (res: GetReportTemplatesQuery) => res
	},
	GetReportTemplateSummaries: {
		providesTags: ["ReportTemplates"] as AddTagTypes,
		transformResponse: (res: GetReportTemplateSummariesQuery) => res
	},
	GetReportVersions: {
		providesTags: ["ReportVersions"] as AddTagTypes,
		transformResponse: (res: GetReportVersionsQuery) => res
	},
	GetSalesComparison: {
		providesTags: ["ReportSalesComparisonData"] as AddTagTypes,
		transformResponse: (res: GetSalesComparisonQuery) => res
	},
	RenameReportFolder: {
		invalidatesTags: ["ReportFolders"] as AddTagTypes,
		transformResponse: (res: RenameReportFolderMutation) => res
	},
	ReportsByAppTag: {
		providesTags: ["Reports"] as AddTagTypes,
		transformResponse: (res: ReportsByAppTagQuery) => res
	},
	RevertReport: {
		invalidatesTags: ["Reports", "ReportVersions"] as AddTagTypes,
		transformResponse: (res: RevertReportMutation) => res
	},
	RunReport: {
		invalidatesTags: ["ReportExecutions"] as AddTagTypes,
		transformResponse: (res: RunReportMutation) => res
	},
	SendInvoiceEmail: {
		invalidatesTags: ["EmailSends"] as AddTagTypes,
		transformResponse: (res: SendInvoiceEmailMutation) => res
	},
	SetOrderEmailRecipientRouting: {
		invalidatesTags: ["OrderEmailRecipientRouting"] as AddTagTypes,
		transformResponse: (res: SetOrderEmailRecipientRoutingMutation) => res
	},
	SetInvoiceEmailChannel: {
		invalidatesTags: ["OrderEmailSettings", "NotificationSettings"] as AddTagTypes,
		transformResponse: (res: SetInvoiceEmailChannelMutation) => res
	},
	SetOrderEmailEnablement: {
		invalidatesTags: ["OrderEmailSettings", "NotificationSettings"] as AddTagTypes,
		transformResponse: (res: SetOrderEmailEnablementMutation) => res
	},
	SetOrderEmailSettings: {
		invalidatesTags: ["OrderEmailSettings"] as AddTagTypes,
		transformResponse: (res: SetOrderEmailSettingsMutation) => res
	},
	SetReportAppTags: {
		invalidatesTags: ["Reports"] as AddTagTypes,
		transformResponse: (res: SetReportAppTagsMutation) => res
	},
	SetReportScheduleEmailSettings: {
		invalidatesTags: ["ReportSchedules"] as AddTagTypes,
		transformResponse: (res: SetReportScheduleEmailSettingsMutation) => res
	},
	UpdateReport: {
		invalidatesTags: ["Reports", "ReportVersions"] as AddTagTypes,
		transformResponse: (res: UpdateReportMutation) => res
	},
	UpdateReportSchedule: {
		invalidatesTags: ["ReportSchedules"] as AddTagTypes,
		transformResponse: (res: UpdateReportScheduleMutation) => res
	}
};
